import { FC, useEffect, useState } from "react"
import { toast } from "react-toastify"

import { useTranslation } from "react-i18next"

import { HtmlEditor } from "components/editors/HtmlEditor"
import { Modal } from "components/modals/Modal"

import {
  normalizeSlugInput,
  slugify,
  validateSlug
} from "features/game-management/helpers/category-slug"

import { CMS_LOCALE_NAMES, SEO_DESCRIPTION_LIMIT, SEO_TITLE_LIMIT } from "services/cms-seo/type"
import { gameService } from "services/game/game.service"
import { CategoryRecord, CategorySeo, CategorySeoEntry } from "services/game/type"
import { SEO_LOCALES } from "services/seo/type"

import { useBrandSite } from "./BrandSiteContext"
import { Counter, Field, FieldRow, ghostButtonClass, inputClass, LocaleChipBar, modalBoxClass, primaryButtonClass, SectionHeading, ToggleRow } from "./CmsUi"

const FALLBACK_LOCALE = "en"

interface CategorySeoModalProps {
  category: CategoryRecord | null
  onClose: () => void
  onSaved: () => void
}

/** Keyed on the category, not its path, so renaming a slug carries the metadata with it. */
export const CategorySeoModal: FC<CategorySeoModalProps> = ({ category, onClose, onSaved }) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const site = useBrandSite()

  const [slug, setSlug] = useState("")
  const [seo, setSeo] = useState<CategorySeo>({})
  const [locale, setLocale] = useState(FALLBACK_LOCALE)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!category) return
    setSlug(category.slug)
    setSeo(category.seo ?? {})
    setLocale(FALLBACK_LOCALE)
  }, [category])

  // Rendered unconditionally to keep the exit animation.
  if (!category) {
    return (
      <Modal open={false} handler={onClose}>
        <div />
      </Modal>
    )
  }

  const entry: CategorySeoEntry = (seo[locale as keyof CategorySeo] as CategorySeoEntry) ?? {}

  const setEntry = (patch: Partial<CategorySeoEntry>) =>
    setSeo((prev) => ({ ...prev, [locale]: { ...entry, ...patch } }))

  /** Plain-text fields only; `footer` has its own editor. */
  const text = (key: "title" | "description" | "h1") => entry[key] ?? ""

  const effectiveSlug = slug || slugify(category.title?.en ?? category.key)
  const slugProblem = validateSlug(effectiveSlug)
  const slugChanged = effectiveSlug !== category.slug

  /** What the slug field says under itself: a problem, a warning, or the URL. */
  const slugHint = () => {
    if (slugProblem === "reserved") {
      return t("cmsSeo.slugReserved", { defaultValue: "That slug is reserved by the site's own routes" })
    }
    if (slugProblem === "invalid") {
      return t("cmsSeo.slugInvalid", { defaultValue: "Lowercase words joined by single hyphens" })
    }
    if (slugChanged) {
      return t("cmsSeo.categorySlugChanged", {
        defaultValue: "The old URL is redirected automatically, so existing links keep working."
      })
    }
    return `${site.host}/en/casino/${effectiveSlug}`
  }

  const submit = async () => {
    if (slugProblem) return

    setSaving(true)
    // A locale with no overridden field resolves to the brand defaults anyway.
    const cleaned: CategorySeo = { ...(seo.noindex ? { noindex: true } : {}) }
    for (const code of SEO_LOCALES) {
      const value = seo[code] as CategorySeoEntry | undefined
      const kept = {
        ...(value?.title ? { title: value.title } : {}),
        ...(value?.description ? { description: value.description } : {}),
        ...(value?.h1 ? { h1: value.h1 } : {}),
        ...(value?.footer?.html?.trim() ? { footer: value.footer } : {})
      }
      if (Object.keys(kept).length > 0) cleaned[code] = kept
    }

    const saved = await gameService.updateCategory(category.key, { slug: effectiveSlug, seo: cleaned })
    setSaving(false)

    if (saved) {
      toast.success(t("cmsSeo.categorySaved", { defaultValue: "Category SEO saved" }) as string)
      onSaved()
      onClose()
    }
  }

  const hasSeo = (code: string) => {
    const value = seo[code as keyof CategorySeo] as CategorySeoEntry | undefined
    return Boolean(value?.title || value?.description || value?.h1 || value?.footer?.html?.trim())
  }

  return (
    <Modal open handler={onClose}>
      <div className={`${modalBoxClass} max-w-2xl`}>
        <h3 className="mb-1 text-lg font-semibold">
          {category.title?.en || category.key}
        </h3>
        <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
          {t("cmsSeo.categorySubtitle", { defaultValue: "Game category — URL and search metadata" })}
        </p>

        <FieldRow>
          <Field
            label={t("cmsSeo.categorySlug", { defaultValue: "URL slug" })}
            hint={slugHint()}
          >
            <div className="flex items-stretch overflow-hidden rounded-lg border border-black/[0.12] bg-white dark:border-white/[0.12] dark:bg-[#0C0C1A]">
              <span className="flex items-center border-r border-black/[0.1] bg-black/[0.03] px-3 font-mono text-xs text-gray-500 dark:border-white/[0.1] dark:bg-white/[0.04] dark:text-gray-400">
                /casino/
              </span>
              <input
                className="flex-1 bg-transparent px-3 py-2 font-mono text-xs text-black outline-none dark:text-white"
                value={slug}
                onChange={(event) => setSlug(normalizeSlugInput(event.target.value))}
                placeholder={slugify(category.title?.en ?? category.key)}
              />
            </div>
          </Field>
        </FieldRow>

        <ToggleRow
          title={t("cmsSeo.field.index", { defaultValue: "Allow search engines to index" })}
          description={t("cmsSeo.categoryNoindexHint", {
            defaultValue: "Off applies to every language — a category hidden from search is hidden in all of them"
          })}
          checked={seo.noindex !== true}
          onChange={() => setSeo((prev) => ({ ...prev, noindex: prev.noindex ? undefined : true }))}
        />

        <div className="mb-4 mt-5">
          <LocaleChipBar
            current={locale}
            present={SEO_LOCALES.filter(hasSeo)}
            onSelect={setLocale}
            onCreate={setLocale}
          />
        </div>

        {locale !== FALLBACK_LOCALE && !hasSeo(locale) && (
          <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
            {t("cmsSeo.categoryInherits", {
              defaultValue: "{{name}} has nothing of its own and serves the English copy. Fill a field to override it.",
              name: CMS_LOCALE_NAMES[locale] ?? locale
            })}
          </p>
        )}

        <FieldRow>
          <Field
            label={t("seo.col.title", { defaultValue: "Title" })}
            counter={<Counter length={text("title").length} limit={SEO_TITLE_LIMIT} />}
          >
            <input className={inputClass} value={text("title")} onChange={(e) => setEntry({ title: e.target.value })} />
          </Field>
        </FieldRow>

        <FieldRow>
          <Field
            label={t("seo.metaDescription", { defaultValue: "Meta description" })}
            counter={<Counter length={text("description").length} limit={SEO_DESCRIPTION_LIMIT} />}
          >
            <textarea
              className={inputClass}
              rows={3}
              value={text("description")}
              onChange={(e) => setEntry({ description: e.target.value })}
            />
          </Field>
        </FieldRow>

        <FieldRow>
          <Field label="H1">
            <input className={inputClass} value={text("h1")} onChange={(e) => setEntry({ h1: e.target.value })} />
          </Field>
        </FieldRow>

        <SectionHeading>{t("cmsSeo.footerCopy", { defaultValue: "SEO copy below the page" })}</SectionHeading>

        <p className="mb-3 text-[11.5px] leading-relaxed text-gray-400 dark:text-gray-500">
          {t("cmsSeo.footerCopyHint", {
            defaultValue:
              "Rendered under the page's own content. The text is in the page source whichever state it starts in, so search engines read it either way — collapsing only keeps it out of a player's way."
          })}
        </p>

        <HtmlEditor
          value={entry.footer?.html ?? ""}
          onChange={(html) => setEntry({ footer: { html, collapsed: entry.footer?.collapsed !== false } })}
          minHeight={140}
        />

        <div className="mt-3">
          <ToggleRow
            title={t("cmsSeo.footerCollapsed", { defaultValue: "Start collapsed" })}
            checked={entry.footer?.collapsed !== false}
            onChange={() =>
              setEntry({
                footer: { html: entry.footer?.html ?? "", collapsed: entry.footer?.collapsed === false }
              })
            }
          />
        </div>

        <div className="modal-action flex-wrap">
          <button type="button" className={ghostButtonClass} onClick={onClose}>
            {t("common.cancel", { defaultValue: "Cancel" })}
          </button>
          <button type="button" className={primaryButtonClass} disabled={saving || Boolean(slugProblem)} onClick={submit}>
            {t("common.save", { defaultValue: "Save" })}
          </button>
        </div>
      </div>
    </Modal>
  )
}
